import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Icon,
  Badge,
  SimpleGrid
} from '@chakra-ui/react'
import { FiArrowRight, FiEdit3, FiCheckCircle, FiFileText, FiLayout } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { AuthModal } from '../components/AuthModal'
import { auth } from '../config/firebase'
import { onAuthStateChanged } from 'firebase/auth'

export const GetStartedPage: React.FC<{ defaultMode?: 'service' | 'product' }> = ({ defaultMode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalMode, setAuthModalMode] = useState<'signup' | 'login'>('signup')
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const defaultOnboardingPath =
    defaultMode === 'product'
      ? '/for-products/onboarding/basic-setup'
      : defaultMode === 'service'
        ? '/for-services/onboarding/basic-setup'
        : '/for-services/onboarding/basic-setup' // Default to services if no mode specified

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user)
      if (user) {
        // If authenticated and came from onboarding, redirect back
        const fromPath = location.state?.from
        const isFromOnboarding = typeof fromPath === 'string' && (
          fromPath.startsWith('/for-services/onboarding') ||
          fromPath.startsWith('/for-products/onboarding')
        )
        if (fromPath && isFromOnboarding) {
          // Only navigate if we're not already on that path
          if (location.pathname !== fromPath) {
            navigate(fromPath, { replace: true })
          }
        } else if (!fromPath && location.pathname.endsWith('/get-started')) {
          // Only navigate if we're still on a get-started page.
          // This prevents loops when already on onboarding pages.
          navigate(defaultOnboardingPath, { replace: true })
        }
      }
    })
    return () => unsubscribe()
    // Only depend on navigate, use location.state and location.pathname directly in callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, defaultOnboardingPath])

  // Listen for Google One Tap success
  useEffect(() => {
    const handleOneTapSuccess = (event: CustomEvent<{ email: string; idToken: string }>) => {
      // Token already stored by App.tsx, just redirect
      const fromPath = location.state?.from || defaultOnboardingPath
      navigate(fromPath)
    }

    window.addEventListener('googleOneTapSuccess', handleOneTapSuccess as EventListener)
    return () => {
      window.removeEventListener('googleOneTapSuccess', handleOneTapSuccess as EventListener)
    }
  }, [navigate, defaultOnboardingPath, location.state?.from])

  const handleStartProfile = () => {
    if (isAuthenticated) {
      const fromPath = location.state?.from || defaultOnboardingPath
      navigate(fromPath)
    } else {
      setShowAuthModal(true)
    }
  }

  const handleAuthSuccess = async (email: string, idToken?: string) => {
    if (idToken) {
      localStorage.setItem('firebase_id_token', idToken)
      localStorage.setItem('user_email', email)
    }
    setShowAuthModal(false)
    // Redirect to onboarding
    const fromPath = location.state?.from || defaultOnboardingPath
    navigate(fromPath)
  }

  const steps = [
    {
      step: 1,
      icon: FiFileText,
      title: "Answer 4 questions + upload photo",
      subtitle: "60-90 seconds",
      description: "Name, who you help, what change you help them achieve, delivery/timeframe (optional), and photo. This enables instant carousel generation.",
      details: ["4 essential questions", "Photo upload (required)", "Optional delivery/timeframe"]
    },
    {
      step: 2,
      icon: FiCheckCircle,
      title: "Preview your carousel instantly",
      subtitle: "Optional refinements",
      description: "See 3 carousel variations (4-7 slides each) generated immediately. Optionally refine story beats: before moment, turning point, approach, first-call expectations.",
      details: ["Instant carousel preview", "3 template variations", "Optional story refinements"]
    },
    {
      step: 3,
      icon: FiLayout,
      title: "Choose CTA + select package",
      subtitle: "Ready to launch",
      description: "Choose primary CTA (Book a Call / Ask a Question), preview landing page, then select a visibility pack. Pay only when you activate a pack.",
      details: ["Choose CTA type", "Preview landing page", "Select visibility pack"]
    }
  ]

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="white">
      <SpotlightHeader />
      
      <Box flex={1}>
        {/* Hero */}
        <Box 
          bgGradient="linear(to-br, brand.50, blue.50)" 
          py={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box
            position="absolute"
            top="-20%"
            right="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="brand.100"
            opacity={0.3}
            filter="blur(100px)"
          />
          <Box
            position="absolute"
            bottom="-20%"
            left="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="blue.100"
            opacity={0.3}
            filter="blur(100px)"
          />

          <Container maxW="6xl" position="relative" zIndex={1}>
            <VStack gap={8} textAlign="center">
              <Badge
                colorPalette="brand"
                size="lg"
                px={4}
                py={2}
                borderRadius="full"
                variant="subtle"
                border="1px solid"
                borderColor="brand.200"
              >
                Get Started
              </Badge>
              <Heading 
                size={{ base: '3xl', md: '4xl' }} 
                color="gray.900" 
                lineHeight="shorter"
                fontWeight="extrabold"
              >
                Ready to get clients without becoming a marketer?
              </Heading>
              <Text 
                fontSize={{ base: 'lg', md: 'xl' }} 
                color="gray.700" 
                lineHeight="tall" 
                maxW="2xl"
              >
                Share your story. We build your page and ads. You approve. We handle the rest. No ad managers, no marketing courses, no complexity.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Steps */}
        <Container maxW="6xl" py={{ base: 12, md: 20 }}>
          <VStack gap={12} align="stretch">
            <Box textAlign="center">
              <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={4}>
                Get Started in 3 Simple Steps
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl" mx="auto">
                From profile to clients—no marketing complexity required
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
              {steps.map((item) => (
                <Card.Root
                  key={item.step}
                  borderWidth="1px"
                  borderColor="gray.200"
                  bg="white"
                  borderRadius="2xl"
                  shadow="sm"
                  _hover={{ shadow: 'lg', borderColor: 'brand.300', transform: 'translateY(-4px)' }}
                  transition="all 0.3s"
                  h="full"
                >
                  <Card.Body p={8}>
                    <VStack align="start" gap={6} h="full">
                      {/* Step Number & Icon */}
                      <HStack gap={4} w="full">
                        <Box
                          bg="brand.500"
                          color="white"
                          borderRadius="full"
                          w={12}
                          h={12}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="xl"
                          flexShrink={0}
                          shadow="md"
                        >
                          {item.step}
                        </Box>
                        <Box
                          p={3}
                          bg="brand.50"
                          borderRadius="lg"
                          color="brand.600"
                        >
                          <Icon as={item.icon} boxSize={6} />
                        </Box>
                      </HStack>

                      {/* Content */}
                      <VStack align="start" gap={2} flex={1}>
                        <Heading size="md" color="gray.900" fontWeight="bold">
                          {item.title}
                        </Heading>
                        <Text fontSize="sm" color="brand.600" fontWeight="medium">
                          {item.subtitle}
                        </Text>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                          {item.description}
                        </Text>
                      </VStack>

                      {/* Details */}
                      <VStack align="start" gap={2} w="full" pt={2} borderTop="1px solid" borderColor="gray.100">
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                          What you'll do:
                        </Text>
                        {item.details.map((detail, i) => (
                          <HStack key={i} gap={2} align="start">
                            <Icon as={FiCheckCircle} color="brand.500" boxSize={4} mt={0.5} flexShrink={0} />
                            <Text fontSize="sm" color="gray.600">{detail}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>

            {/* CTA Card */}
            <Card.Root
              id="ready-to-get-started"
              borderWidth="2px"
              borderColor="brand.200"
              bgGradient="linear(to-br, brand.50, blue.50)"
              borderRadius="2xl"
              shadow="lg"
              overflow="hidden"
            >
              <Card.Body p={{ base: 6, md: 10 }}>
                <VStack gap={6} textAlign="center">
                  <VStack gap={3}>
                    <Heading size={{ base: 'xl', md: '2xl' }} color="gray.900">
                      Ready to get started?
                    </Heading>
                    <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700" maxW="2xl">
                      Complete your profile and interview questions. We'll build your Spotlight page and you approve everything before activating visibility.
                    </Text>
                  </VStack>
                  
                  {/* Auth Buttons - Prominent for non-authenticated users */}
                  <VStack gap={4} w="full" maxW="md" mx="auto">
                    <Button
                      onClick={handleStartProfile}
                      size="xl"
                      colorPalette="brand"
                      fontWeight="bold"
                      px={12}
                      py={6}
                      fontSize="lg"
                      w="full"
                      _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                      transition="all 0.2s"
                    >
                      <HStack gap={2}>
                        <Icon as={FiEdit3} />
                        <Text>Create Account</Text>
                        <Icon as={FiArrowRight} />
                      </HStack>
                    </Button>

                    <HStack gap={4} w="full" align="center">
                      <Box flex={1} h="1px" bg="gray.300" />
                      <Text fontSize="sm" color="gray.500" fontWeight="medium">
                        OR
                      </Text>
                      <Box flex={1} h="1px" bg="gray.300" />
                    </HStack>

                    <Button
                      onClick={() => {
                        setAuthModalMode('login')
                        setShowAuthModal(true)
                      }}
                      size="xl"
                      variant="outline"
                      colorPalette="brand"
                      fontWeight="bold"
                      px={12}
                      py={6}
                      fontSize="lg"
                      w="full"
                      borderWidth="2px"
                      _hover={{ 
                        transform: 'translateY(-2px)', 
                        shadow: 'lg',
                        bg: 'brand.50',
                        borderColor: 'brand.400'
                      }}
                      transition="all 0.2s"
                    >
                      Sign In
                    </Button>
                  </VStack>

                  <Text fontSize="sm" color="gray.500">
                    No credit card required • Pay only when you activate visibility
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>
          </VStack>
        </Container>
      </Box>

      <SpotlightFooter />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
        mode={authModalMode}
      />
    </Box>
  )
}
